import { cn } from '@/lib/utils';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { getVatCopy } from '@/lib/i18n/vat';
import { useTaxRates } from '@/hooks/useTaxRates';
import { calculateTax } from '@/lib/taxCalculator';

interface VatNoteProps {
  /** Gross amount (never changed — only used to derive the VAT share) */
  amount?: number;
  /** ISO country code of the delivery address; omit for a generic note */
  countryCode?: string;
  /** B2B with a valid EU VAT ID → reverse charge */
  isB2B?: boolean;
  /** Show the VAT amount in brackets (requires `amount`) */
  showAmount?: boolean;
  className?: string;
}

/**
 * Small, purely informational VAT line shown under a price.
 * Never influences any price calculation.
 */
export function VatNote({ amount, countryCode, isB2B = false, showAmount = false, className }: VatNoteProps) {
  const { language } = useLanguage();
  const copy = getVatCopy(language);
  const { taxRates, useLocalRates } = useTaxRates();

  // Without a delivery country we show the shop's home rate (DE) — standard
  // practice for consumer prices before an address is known.
  const effectiveCountry = countryCode || 'DE';
  const calc = calculateTax(amount ?? 0, effectiveCountry, taxRates, isB2B, 'standard', useLocalRates);

  let text: string;

  if (calc.taxRate === 0) {
    text = calc.isEU ? copy.reverseCharge : copy.exportFree;
  } else if (showAmount && amount != null && amount > 0) {
    const formatted = new Intl.NumberFormat(language === 'zh' ? 'zh-CN' : language, {
      style: 'currency',
      currency: 'EUR',
    }).format(calc.taxAmount);
    text = copy.inclRateAmount
      .replace('{rate}', String(calc.taxRate))
      .replace('{amount}', formatted);
  } else {
    text = copy.inclRate.replace('{rate}', String(calc.taxRate));
  }

  return (
    <p className={cn('text-[11px] leading-tight text-muted-foreground', className)}>{text}</p>
  );
}

export default VatNote;
